"use client";

import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@libs/axios";
import { usePPJKContext } from "@contexts/PPJKContext";

interface UsageDashboardGraphParams {
  ppjkName?: string;
  /** A super admin's picked PPJK; the server ignores it for everyone else. */
  ppjkNpwp?: string;
  startDate?: string;
  endDate?: string;
}

interface UsageDashboardGraphItem {
  date: string;
  totalAju: number;
}

interface UsageDashboardGraphResponse {
  success: boolean;
  message: string;
  data: {
    data: UsageDashboardGraphItem[];
  };
}

function isDemoUser(): boolean {
  try {
    const authData = localStorage.getItem("authData");
    if (authData) {
      const parsed = JSON.parse(authData);
      const email = parsed.email?.toLowerCase() || "";
      return email.includes("demo");
    }
  } catch {
    // ignore
  }
  return false;
}

function getDemoGraphData(): UsageDashboardGraphItem[] {
  return [
    { date: "2024-11-25", totalAju: 2 },
    { date: "2024-11-26", totalAju: 1 },
    { date: "2024-11-27", totalAju: 3 },
    { date: "2024-11-28", totalAju: 2 },
    { date: "2024-11-29", totalAju: 4 },
    { date: "2024-11-30", totalAju: 1 },
    { date: "2024-12-01", totalAju: 5 },
    { date: "2024-12-02", totalAju: 3 },
    { date: "2024-12-03", totalAju: 2 },
  ];
}

async function fetchUsageDashboardGraph({
  ppjkName,
  ppjkNpwp,
  startDate,
  endDate,
  signal,
}: UsageDashboardGraphParams & { signal?: AbortSignal }): Promise<UsageDashboardGraphItem[]> {
  if (isDemoUser()) {
    return getDemoGraphData();
  }

  // Check for access token
  if (!window.localStorage.getItem("accessToken") && !document.cookie.includes("accessToken")) {
    console.warn("No access token available, skipping dashboard API call");
    return [];
  }

  const { data: response } = await axiosInstance.post<UsageDashboardGraphResponse>(
    "/api/v1/dashboard/usage-dashboard-graph",
    {
      ppjkName,
      ppjkNpwp,
      startDate,
      endDate,
    },
    { signal }
  );

  return response.data?.data ?? [];
}

export default function useGetUsageDashboardGraph(params?: UsageDashboardGraphParams) {
  const { getPpjkWithFallback } = usePPJKContext();

  const ppjkName = params?.ppjkName || getPpjkWithFallback() || undefined;

  const queryResult = useQuery({
    queryKey: [
      "usage-dashboard-graph",
      {
        ppjkName,
        ppjkNpwp: params?.ppjkNpwp,
        startDate: params?.startDate,
        endDate: params?.endDate,
      },
    ],
    queryFn: ({ signal }) =>
      fetchUsageDashboardGraph({
        ...params,
        ppjkName,
        signal,
      }),
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
  });

  return {
    data: queryResult.data,
    loading: queryResult.isLoading || queryResult.isFetching,
    error: queryResult.error?.message ?? "",
  };
}

export type { UsageDashboardGraphItem, UsageDashboardGraphParams };
